import dayjs from 'dayjs';

const num = (value) => (typeof value === 'number' ? value : 0);

/** Rows processed so far across both systems (ok + fail + skip). */
export const getRunProcessed = (run) => (
  num(run?.cin7Ok) + num(run?.cin7Fail) + num(run?.cin7Skip)
  + num(run?.mondayOk) + num(run?.mondayFail)
);

/** Total rows expected across both systems. */
export const getRunTotal = (run) => num(run?.cin7Total) + num(run?.mondayTotal);

/** Overall apply progress as a whole-number percentage (0 when nothing to do yet). */
export const getApplyPercent = (run) => {
  const total = getRunTotal(run);
  if (!total) return 0;
  return Math.min(100, Math.round((getRunProcessed(run) / total) * 100));
};

/** Percentage for a single system given ok/fail/skip and a total. */
export const getSystemPercent = (done, total) => {
  if (!total) return 0;
  return Math.min(100, Math.round((num(done) / num(total)) * 100));
};

/** First present timestamp from a list of candidate camelCase field names. */
export const pickTimestamp = (run, names) => {
  if (!run) return null;
  for (let i = 0; i < names.length; i += 1) {
    if (run[names[i]]) return run[names[i]];
  }
  return null;
};

export const formatTimestamp = (value) => (value ? dayjs(value).format('DD/MM/YYYY h:mma') : '—');

export const formatDateOnly = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '—');

/** Human duration from total seconds (e.g. "12m 34s"). */
export const formatDurationSeconds = (totalSec) => {
  if (totalSec == null || !Number.isFinite(Number(totalSec)) || Number(totalSec) < 0) {
    return null;
  }
  const secsTotal = Math.round(Number(totalSec));
  const mins = Math.floor(secsTotal / 60);
  const secs = secsTotal % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hours}h ${remMins}m`;
  }
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

/** Human duration between two timestamps (e.g. "12m 34s"). */
export const formatDuration = (start, end) => {
  if (!start || !end) return null;
  const ms = dayjs(end).diff(dayjs(start));
  if (!Number.isFinite(ms) || ms < 0) return null;
  return formatDurationSeconds(ms / 1000);
};

/** Whole days between two date-like values (through - maxInvoice). */
export const daysBetween = (later, earlier) => {
  if (!later || !earlier) return null;
  const a = dayjs(later).startOf('day');
  const b = dayjs(earlier).startOf('day');
  if (!a.isValid() || !b.isValid()) return null;
  return a.diff(b, 'day');
};

/** Whole-number percentage with explicit sign from a fraction (0.052 -> "+5%"). */
export const formatSignedPct = (fraction) => {
  if (fraction == null || !Number.isFinite(Number(fraction))) return null;
  const pct = Math.round(Number(fraction) * 100);
  if (pct > 0) return `+${pct}%`;
  if (pct < 0) return `-${Math.abs(pct)}%`;
  return '0%';
};

/** Thousands-separated integer ("2,422"). */
export const formatCount = (value) => (
  value == null ? '0' : Math.round(Number(value)).toLocaleString('en')
);

const pluralCustomers = (n) => `${formatCount(n)} ${n === 1 ? 'customer' : 'customers'}`;

/** Labels for the rep mover buckets, in display order. Matches classifyMover on the backend. */
const MOVER_GROUPS = [
  { kind: 'newChampion', label: 'New Champions' },
  { kind: 'outOfAtRisk', label: 'Out of At Risk' },
  { kind: 'intoAtRisk', label: 'Into At Risk' },
  { kind: 'developing', label: 'Developing' },
];

/** Channels not worth surfacing to reps (blank / Undefined tags). */
const HIDDEN_CHANNELS = new Set(['Other']);

/**
 * Plain-text, copy-paste update for the sales rep channel.
 * Team block first, then a short block per rep. Lines are omitted when zero/missing.
 * @param {object} summaryJson run.summaryJson (needs repDigest + optional scoring.referenceDate)
 * @param {string} regionLabel human region label ("New Zealand")
 * @param {string|Date} [fallbackDate] used for the month header when referenceDate is absent
 * @returns {string|null} null when repDigest is missing
 */
export const buildRepUpdateText = (summaryJson, regionLabel, fallbackDate) => {
  const digest = summaryJson?.repDigest;
  if (!digest || typeof digest !== 'object') return null;
  const team = digest.team || {};
  const channels = Array.isArray(digest.channels) ? digest.channels : [];
  const reps = Array.isArray(digest.reps) ? digest.reps : [];

  const refDate = summaryJson?.scoring?.referenceDate || fallbackDate;
  const month = refDate && dayjs(refDate).isValid() ? dayjs(refDate).format('MMMM') : null;
  const lines = [];
  lines.push(['Trade customer update', regionLabel, month].filter(Boolean).join(' - '));
  lines.push('');

  const teamPct = formatSignedPct(team.fyPct);
  if (teamPct) {
    lines.push(
      `Overall: FY forecast tracking ${teamPct} vs last FY across `
      + `${formatCount(team.customers ?? 0)} trade customers`,
    );
  } else if (team.customers) {
    lines.push(`Overall: ${formatCount(team.customers)} trade customers scored`);
  }

  const channelParts = channels
    .filter((c) => c && !HIDDEN_CHANNELS.has(c.channel) && formatSignedPct(c.fyPct))
    .map((c) => `${c.channel} ${formatSignedPct(c.fyPct)}`);
  if (channelParts.length) lines.push(`Channels: ${channelParts.join(' | ')}`);

  const wins = [];
  if (team.outOfAtRisk) wins.push(`${pluralCustomers(team.outOfAtRisk)} moved out of At Risk`);
  if (team.newChampions) {
    wins.push(`${formatCount(team.newChampions)} new ${team.newChampions === 1 ? 'Champion' : 'Champions'}`);
  }
  if (wins.length) lines.push(`Wins: ${wins.join(' | ')}`);

  const watch = [];
  if (team.intoAtRisk) watch.push(`${pluralCustomers(team.intoAtRisk)} slipped into At Risk`);
  if (team.championsToAtRisk) {
    watch.push(
      `${formatCount(team.championsToAtRisk)} ${team.championsToAtRisk === 1 ? 'Champion' : 'Champions'} now At Risk`,
    );
  }
  if (watch.length) lines.push(`Watch: ${watch.join(' | ')}`);

  if (reps.length) {
    lines.push('');
    reps.forEach((rep) => {
      const head = [`${rep.rep} - ${pluralCustomers(rep.customers ?? 0)}`];
      const repPct = formatSignedPct(rep.fyPct);
      if (repPct) head.push(`FY ${repPct}`);
      if (rep.up || rep.down) head.push(`${rep.up ?? 0} up / ${rep.down ?? 0} down`);
      lines.push(head.join(' | '));

      const movers = Array.isArray(rep.movers) ? rep.movers : [];
      MOVER_GROUPS.forEach(({ kind, label }) => {
        const names = movers
          .filter((m) => m && m.kind === kind)
          .map((m) => m.customerName || m.customerId)
          .filter(Boolean);
        if (names.length) lines.push(`  ${label}: ${names.join(', ')}`);
      });
    });
  }

  return lines.join('\n');
};
